import { Options, XAxisOptions } from 'highcharts';
import { IGetOptionsProps } from './types';

export const dateTimeLabelFormats = {
  millisecond: '%H:%M:%S.%L',
  second: '%H:%M:%S',
  minute: '%H:%M',
  hour: '%H:%M',
  day: '%e. %b',
  week: '%e. %b',
  month: "%b '%y",
  year: '%Y',
};

export const defaultLegendOptions: Options['legend'] = {
  enabled: false,
  padding: 2,
  margin: 3,
  itemDistance: 20,
  symbolRadius: 0,
  itemStyle: { fontWeight: '500', opacity: 0.8 },
  itemHoverStyle: { fontWeight: '500', opacity: 1 },
};
const HORIZONTAL_MOUSE_OFFSET = 55;
const VERTICAL_MOUSE_OFFSET = 45;
const SIDENAV_WIDTH = 64;

export const getDefaultTooltipSettings = (isMulti: boolean): Options['tooltip'] => ({
  positioner(labelWidth, labelHeight, point) {
    // Get the current mouse position within the chart
    const mousePosition = this.chart.mouseCoords;

    // If mousePosition is undefined, use point parameter for positioning
    if (!mousePosition) {
      if (point && typeof point.plotX === 'number' && typeof point.plotY === 'number') {
        return {
          x: point.plotX + this.chart.plotLeft,
          y: point.plotY + this.chart.plotTop,
        };
      }

      // If no valid point, position at chart center
      return {
        x: this.chart.chartWidth / 2,
        y: this.chart.chartHeight / 2,
      };
    }

    // Get the bounding rectangle of the chart container
    const chartRect = this.chart.container.getBoundingClientRect();

    // Access the tooltip container and its content to calculate dimensions
    const tooltipContainer = this.chart?.tooltip?.container?.querySelector('div span div');
    const tooltipContent = tooltipContainer?.querySelector(':scope > *');
    const contentRect = (tooltipContent || tooltipContainer)?.getBoundingClientRect();

    // Get the bounding rectangle of the tooltip wrapper
    const wrapperRect = this.chart.tooltip.container?.getBoundingClientRect();

    if (!wrapperRect || !contentRect) {
      // If we can't get the dimensions, default to center positioning
      return {
        x: this.chart.chartWidth / 2,
        y: this.chart.chartHeight / 2,
      };
    }

    // Calculate the vertical offset between the tooltip content and its wrapper
    const deltaY = contentRect.top - wrapperRect.top;

    // Initialize tooltip position next to the mouse pointer
    const mousePositionX = mousePosition.x ?? 0;
    const mousePositionY = mousePosition.y ?? 0;
    let tooltipX = mousePositionX + HORIZONTAL_MOUSE_OFFSET; // Horizontal offset from the mouse pointer
    let tooltipY = mousePositionY; // Align vertically with the mouse pointer

    // Adjust horizontal position if the mouse is on the right half of the chart
    if (mousePositionX > this.chart.plotWidth / 2) {
      tooltipX = mousePositionX - contentRect.width - HORIZONTAL_MOUSE_OFFSET;

      // Check if tooltip would overlap with sidenav or go beyond left edge
      const tooltipLeftInViewport = chartRect.left + tooltipX;

      if (tooltipLeftInViewport < SIDENAV_WIDTH) {
        // Position to the right of mouse instead
        tooltipX = mousePositionX + HORIZONTAL_MOUSE_OFFSET;
      }
    }

    // Calculate the bottom position of the tooltip in the viewport
    const tooltipBottom = chartRect.top + tooltipY + contentRect.height + deltaY;

    // Adjust vertical position to ensure the tooltip doesn't go out of the viewport
    if (tooltipBottom > window.innerHeight) {
      tooltipY -= tooltipBottom - window.innerHeight;
    }

    // Return the calculated tooltip position
    return {
      x: tooltipX,
      y: tooltipY,
    };
  },
  outside: true, //  nessesery to render the tooltip out of bounds
  split: false, // nessesery props to unify tooltips and hide them
  shared: isMulti,
  shadow: false,
  hideDelay: 100,
  style: {
    pointerEvents: 'none', // nessesery for rendering react component
  },
});

export const getDefaultCrosshairOptions = (
  crosshair: XAxisOptions['crosshair'],
  appTheme: IGetOptionsProps['appTheme'],
): XAxisOptions['crosshair'] => {
  if (crosshair !== undefined) {
    return crosshair;
  }

  return {
    width: 2,
    color: appTheme?.graph.crosshairColor,
    dashStyle: 'Dash',
  };
};
